import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, call } from '@/lib/api';
import { queryClient } from '@/lib/query';
import { invalidateNavCounters } from '@/models/nav-counters';
import type { TInvoicingCompany } from '@/models/renewals';

export type OfferStatus =
  | 'draft'
  | 'sent'
  | 'in-progress'
  | 'plan-proposed'
  | 'accepted'
  | 'expired'
  | 'invoice-paid';

export type TOfferStatusInfo = { status: OfferStatus; detail: string | null };

export const offerStatusColor: Record<OfferStatus, string> = {
  draft: 'gray',
  sent: 'blue',
  'in-progress': 'orange',
  'plan-proposed': 'violet',
  accepted: 'green',
  expired: 'red',
  'invoice-paid': 'teal',
};

export const offerStatusLabel: Record<OfferStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  'in-progress': 'In Progress',
  'plan-proposed': 'Plan Proposed',
  accepted: 'Accepted',
  expired: 'Expired',
  'invoice-paid': 'Invoice Paid',
};

export type TOfferItem = {
  id: number;
  offerId: number;
  itemType: 'proposal' | 'service';
  cpId: number | null;
  serviceId: number | null;
  priceId: number | null;
  quantity: number;
  weight: number;
  addedAt: string;
  addedBy: number;
  itemName: string;
  jurisdiction: string | null;
  currency: string | null;
  isDraft: boolean | null;
  isDeleted: boolean | null;
  isArchived: boolean | null;
  priceAmount: number | null;
  serviceType: string | null;
  serviceIndustry: string | null;
  renewal: string | null;
};

export type TSelectionService = {
  serviceId: number;
  serviceName: string;
  priceAmount: number;
  priceCurrency: string;
  selected: boolean;
  tiers: { included: boolean; displayIcon: boolean }[];
  children: { serviceId: number; serviceName: string }[];
  renewal: {
    period: string;
    renewalPrice: number;
    renewalCurrency: string;
  } | null;
};

export type TSelectionBlock = {
  uuid: string;
  title: string;
  type: string;
  selectedTier: number | null;
  selectedPackageName: string | null;
  tierNames: string[];
  /** The block states a total per package, as the customer's page and the PDF do. */
  showSummaryRow: boolean;
  services: TSelectionService[];
};

export type TSelectionProposal = {
  cpId: number;
  cpName: string;
  jurisdiction: string;
  currency: string;
  blocks: TSelectionBlock[];
};

export type TCustomerSelections = {
  source: 'snapshot' | 'live';
  capturedAt: string | null;
  paymentMethod: string | null;
  paymentCurrency: string | null;
  clientType: string | null;
  clientDetails: Record<string, unknown> | null;
  invoiceCurrency: string | null;
  dueConversionRate: number | null;
  proposals: TSelectionProposal[];
  standaloneServices: TSelectionService[];
};

export type TPayer = {
  id: number;
  clientType: 'Individual' | 'Corporate';
  countryCode: string;
  displayName: string;
  kycApprovedAt: string | null;
  kycExpiresAt: string | null;
  mondayPayerId: number | null;
  deletedAt?: string | null;
  source?: 'local' | 'monday';
  mondayKycItemId?: number;
};

export type TOffer = {
  id: number;
  uuid: string;
  title: string;
  contactId: number;
  contactName: string;
  contactEmail: string;
  accepted: boolean;
  acceptedAt: string | null;
  expiresAt: string | null;
  sentAt: string | null;
  discountPercentage: number;
  /** The discount's own deadline; null means it lasts as long as the offer. */
  discountExpiresAt: string | null;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  payerId: number | null;
  payer: TPayer | null;
  accountId: number | null;
  accountName: string | null;
  clientCompanyId?: number | string | null;
  portfolioOwner?: {
    kind: 'client_company' | 'contact';
    id: number | string;
  } | null;
  items: TOfferItem[];
  customerSelections: TCustomerSelections | null;
  invoiceStatus: string | null;
  kycStatus: string | null;
  kycSessionId?: number | null;
  currencyTotals?: Record<string, { paid: number; outstanding: number }> | null;
  splitMode?: string | null;
  splitEnabled?: boolean | null;
  splitSelection?: unknown | null;
  splitPlanStatus?: string | null;
  paidServices?: number | null;
  totalServices?: number | null;
  /** The contact record's address today; offers still send to contactEmail. */
  currentContactEmail?: string | null;
  /** Derived from the offer's attached payer, before any invoice exists. */
  prospectiveInvoiceRecipient?: TInvoiceRecipient | null;
  archivedAt?: string | null;
  isTest: boolean;
  /** Whether this user may Unlock the offer: its owner, a member of its contact, or offers:access-all. */
  mayUnlock?: boolean;
  /** Accepted, and a cancelled proforma left a slot that Issue new proforma fills. */
  noActiveProforma?: boolean | null;
  /** Accepted offers only: what is payable now and what Issue new proforma would fill. */
  proformaState?: TOfferProformaState | null;
};

export type TOfferListItem = {
  id: number;
  uuid: string;
  title: string;
  contactId: number;
  contactName: string;
  contactEmail: string;
  accepted: boolean;
  acceptedAt: string | null;
  expiresAt: string | null;
  sentAt: string | null;
  discountPercentage: number;
  createdAt: string;
  itemCount: number;
  invoiceStatus: string | null;
  currencyTotals: Record<string, { paid: number; outstanding: number }> | null;
  splitMode?: string | null;
  paidServices?: number | null;
  totalServices?: number | null;
  kycStatus: string | null;
  createdByEmail: string | null;
  createdByName: string | null;
  createdByPicture: string | null;
  accountId: number | null;
  accountName: string | null;
  splitPlanStatus: string | null;
  splitEnabled?: boolean | null;
  splitSelection?: unknown | null;
  archivedAt: string | null;
  isTest: boolean;
  /** Accepted, and a cancelled proforma left a slot that Issue new proforma fills. */
  noActiveProforma?: boolean | null;
};

export type TOfferProformaState = {
  state: 'payable' | 'no-payable-proforma' | 'settled' | 'awaiting-installment';
  target: { id: number; invoiceNumber: string; paymentMethod: string } | null;
  newProforma: {
    planItemId: number | null;
    seq: number | null;
    cancelledInvoiceId: number;
  } | null;
};

export const TEST_CONTACT_DOMAIN = '@legalbison.com';

export const isTestContactEmail = (email: string) =>
  email.trim().toLowerCase().endsWith(TEST_CONTACT_DOMAIN);

export type TestScope = 'live' | 'test' | 'all';

export type TOfferActivity = {
  action: number;
  time: string;
  meta: Record<string, unknown>;
  source: 'customer' | 'user' | 'email';
};

export type TInvoiceRecipient = {
  email: string;
  kind: 'payer';
  blocked: boolean;
  blockReason?: string;
};

// A KYC session in one of these states means the customer has committed and is
// mid-verification (the standard-flow equivalent of preparing a split plan).
// 'pending' (link generated, not started) and 'approved' (done) are excluded so
// they fall through to the ambient sent/expired/draft phase.
const KYC_ACTIVE = new Set(['in_progress', 'retry', 'rejected']);

// Derives the offer's lifecycle phase from stored signals, first match wins.
// Returns a coarse main `status` (drives the ribbon word + color) plus a granular
// `detail` line. Phases 3-6 (customer committed) sit ABOVE the expiry check on
// purpose: a live deal in plan-prep or KYC must never flip back to Expired.
export function getOfferStatus(offer: {
  accepted: boolean;
  sentAt: string | null;
  expiresAt: string | null;
  invoiceStatus?: string | null;
  splitEnabled?: boolean | null;
  splitSelection?: unknown | null;
  splitPlanStatus?: string | null;
  kycStatus?: string | null;
  noActiveProforma?: boolean | null;
  proformaState?: { state: string } | null;
}): TOfferStatusInfo {
  if (
    offer.accepted &&
    (offer.noActiveProforma ||
      offer.proformaState?.state === 'no-payable-proforma')
  ) {
    return { status: 'accepted', detail: 'No active proforma' };
  }
  if (offer.invoiceStatus === 'paid' || offer.invoiceStatus === 'fully-paid') {
    return { status: 'invoice-paid', detail: null };
  }
  if (offer.accepted) {
    return {
      status: 'accepted',
      detail: offer.splitEnabled ? 'Installments active' : 'Invoice issued',
    };
  }
  const splitConfirmed = !!offer.splitEnabled && offer.splitSelection != null;
  if (splitConfirmed) {
    if (offer.splitPlanStatus === 'approved') {
      return { status: 'plan-proposed', detail: 'Awaiting customer approval' };
    }
    if (offer.splitPlanStatus === 'proposed') {
      return { status: 'in-progress', detail: 'Plan in review' };
    }
    if (offer.splitPlanStatus === 'returned') {
      return { status: 'in-progress', detail: 'Plan returned by BizDev' };
    }
    return { status: 'in-progress', detail: 'Preparing split payment plan' };
  }
  if (
    !offer.splitEnabled &&
    offer.kycStatus &&
    KYC_ACTIVE.has(offer.kycStatus)
  ) {
    return { status: 'in-progress', detail: 'KYC in progress' };
  }
  if (offer.expiresAt && new Date(offer.expiresAt) < new Date()) {
    return { status: 'expired', detail: null };
  }
  if (offer.sentAt) return { status: 'sent', detail: 'Awaiting customer' };
  return { status: 'draft', detail: 'Not sent' };
}

const callJson = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { json }).then((r) => r.json());

const callGet = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { method: 'GET', json }).then((r) => r.json());

export const offerKeys = {
  all: ['offers'] as const,
  lists: () => [...offerKeys.all, 'list'] as const,
  list: (params: any) => [...offerKeys.lists(), params] as const,
  details: () => [...offerKeys.all, 'detail'] as const,
  detail: (id: number) => [...offerKeys.details(), id] as const,
  activity: (id: number) => [...offerKeys.all, 'activity', id] as const,
  comments: (id: number) => [...offerKeys.all, 'comments', id] as const,
  legalRequests: (offerId: number) =>
    [...offerKeys.all, 'legal-requests', offerId] as const,
};

export type TOfferComment = {
  id: number;
  offerId: number;
  authorUserId: number | null;
  authorEmail: string | null;
  body: string;
  createdAt: string;
};

export function useOfferComments(offerId: number | undefined) {
  return useQuery({
    queryKey: offerKeys.comments(offerId!),
    queryFn: () =>
      callGet<{ comments: TOfferComment[] }>('offer/comments', {
        offerId,
      }).then((r) => r.comments),
    enabled: !!offerId,
  });
}

export function useAddOfferComment() {
  return useMutation({
    mutationFn: (data: { offerId: number; body: string }) =>
      callJson<{ comment: TOfferComment }>('offer/comment', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.comments(vars.offerId),
      });
    },
  });
}

export function useDeleteOfferComment() {
  return useMutation({
    mutationFn: (data: { id: number; offerId: number }) =>
      callJson<{ ok: true }>('offer/comment-delete', { id: data.id }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.comments(vars.offerId),
      });
    },
  });
}

export interface OfferListParams {
  ownerUserId?: number[];
  cpId?: number[];
  page?: number;
  limit?: number;
  accepted?: boolean;
  includeArchived?: boolean;
  contactId?: number;
  splitPlanStatus?:
    | 'draft'
    | 'proposed'
    | 'returned'
    | 'approved'
    | 'committed';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  scope?: TestScope;
}

export interface OfferListResponse {
  offers: TOfferListItem[];
  total: number;
  counts: { live: number; test: number };
  page: number;
  limit: number;
}

export function useOffers(params: OfferListParams = {}) {
  // The list endpoint reads ownerUserId as one comma-separated value; left as an
  // array the query builder would repeat the key and only the last id would land.
  const { ownerUserId, cpId, ...rest } = params;
  const query = {
    ...rest,
    ...(ownerUserId?.length ? { ownerUserId: ownerUserId.join(',') } : {}),
    ...(cpId?.length ? { cpId: cpId.join(',') } : {}),
  };
  return useQuery({
    queryKey: offerKeys.list(params),
    queryFn: () => callGet<OfferListResponse>('offer/list', query),
  });
}

export interface TOfferProposalOption {
  id: number;
  name: string;
  jurisdiction: string;
}

export function useOfferProposalOptions() {
  return useQuery({
    queryKey: [...offerKeys.all, 'proposal-options'],
    queryFn: () =>
      callGet<{ proposals: TOfferProposalOption[] }>('offer/proposal-options'),
  });
}

export function useOffer(id: number | undefined) {
  return useQuery({
    queryKey: offerKeys.detail(id!),
    queryFn: () => callGet<TOffer>('offer/details', { id }),
    enabled: !!id,
  });
}

export function useOfferActivity(offerId: number | undefined) {
  return useQuery({
    queryKey: offerKeys.activity(offerId!),
    queryFn: () => callGet<TOfferActivity[]>('offer/activity', { offerId }),
    enabled: !!offerId,
    refetchInterval: (q) =>
      (q.state.data as TOfferActivity[] | undefined)?.some(
        (a) =>
          a.source === 'email' &&
          ['queued', 'submission_unknown'].includes(
            (a.meta as { status?: string }).status ?? '',
          ),
      )
        ? 15000
        : false,
  });
}

export type TServiceCogs = {
  serviceId: number;
  cogsAmount: number;
  cogsEur: number;
  cogsEstimated: boolean;
};

export function useOfferServiceCogs(
  services: { serviceId: number; priceCurrency: string; priceAmount: number }[],
  enabled = true,
) {
  const key = services
    .map((s) => s.serviceId)
    .sort((a, b) => a - b)
    .join(',');
  return useQuery({
    queryKey: [...offerKeys.all, 'service-cogs', key],
    queryFn: () => callJson<TServiceCogs[]>('offer/service-cogs', { services }),
    enabled: enabled && services.length > 0,
  });
}

export function useCreateOffer() {
  return useMutation({
    mutationFn: (data: {
      title: string;
      contactId: number;
      isTest?: boolean;
    }) => callJson<TOffer>('offer/create', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
    },
  });
}

export function useArchiveOffer() {
  return useMutation({
    mutationFn: (data: { offerId: number; archived: boolean }) =>
      callJson<{ id: number; archivedAt: string | null }>(
        'offer/archive',
        data,
      ),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
      invalidateNavCounters();
    },
  });
}

export function useUpdateOffer() {
  return useMutation({
    mutationFn: (data: {
      offerId: number;
      title?: string;
      expiresAt?: string | null;
      discountPercentage?: number;
      discountExpiresAt?: string | null;
    }) => callJson<TOffer>('offer/update', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
    },
  });
}

export function useAddProposal() {
  return useMutation({
    mutationFn: (data: {
      offerId: number;
      cpId: number;
      invalidateSplitPlan?: boolean;
    }) => callJson<TOfferItem>('offer/add-proposal', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
    },
  });
}

export function useAddService() {
  return useMutation({
    mutationFn: (data: {
      offerId: number;
      serviceId: number;
      priceId?: number;
      invalidateSplitPlan?: boolean;
    }) => callJson<TOfferItem>('offer/add-service', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
    },
  });
}

export function useRemoveItem() {
  return useMutation({
    mutationFn: (data: {
      offerId: number;
      itemId: number;
      invalidateSplitPlan?: boolean;
    }) => call('offer/remove-item', { json: data }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
    },
  });
}

export function useReorderItems() {
  return useMutation({
    mutationFn: (data: {
      offerId: number;
      items: { itemId: number; weight: number }[];
    }) => callJson('offer/reorder', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
    },
  });
}

export function useSendOffer() {
  return useMutation({
    mutationFn: (data: { offerId: number }) =>
      call('offer/send', { json: data }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
    },
  });
}

export function useReSendOffer() {
  return useMutation({
    mutationFn: (data: { offerId: number }) =>
      call('offer/re-send', { json: data }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
    },
  });
}

export function useDuplicateOffer() {
  return useMutation({
    mutationFn: (data: {
      offerId: number;
      newContactId: number;
      newTitle?: string;
    }) => callJson<TOffer>('offer/duplicate', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
    },
  });
}

export function useIssueNewProforma() {
  return useMutation({
    mutationFn: (data: { offerId: number; requestKey: string }) =>
      callJson<{
        invoiceId: number;
        invoiceNumber: string;
        issued: boolean;
        seq: number | null;
      }>('offer/issue-new-proforma', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['split-plan'] });
    },
  });
}

export function useRevertOffer() {
  return useMutation({
    mutationFn: (data: { offerId: number }) =>
      call('offer/revert', { json: data }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useExtendExpiry() {
  return useMutation({
    mutationFn: (data: { offerId: number; expiresAt: string }) =>
      callJson<TOffer>('offer/extend-expiry', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
    },
  });
}

/**
 * The 409 body when a verification is running on the offer.
 *
 * `boundToCurrentPayer` separates "this party is mid-verification" from "the
 * customer is verifying, but no party is attached yet" - the second is the
 * ordinary shape of a self-service offer, and wording it as the first told
 * staff to cancel a verification for a party the screen showed as unset.
 */
export interface VerificationInFlightBody {
  code: 'verification-in-flight';
  error: string;
  boundToCurrentPayer: boolean;
  /** Whether THIS user may override it; the override is a privileged act. */
  canOverride: boolean;
}

/** The refusal if it is the overridable one, else null. */
export function verificationInFlight(
  e: unknown,
): VerificationInFlightBody | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  try {
    const body = JSON.parse(e.body);
    return body?.code === 'verification-in-flight' ? body : null;
  } catch {
    return null;
  }
}

/** Overriding ends somebody's verification, so it always carries a reason. */
type PayerChangeOverride = { cancelVerification?: true; reason?: string };

export function useAttachPayer() {
  return useMutation({
    mutationFn: (
      data: {
        offerId: number;
        payerId?: number;
        mondayKycItemId?: number;
      } & PayerChangeOverride,
    ) => callJson('offer/attach-payer', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
    },
  });
}

export function useDetachPayer() {
  return useMutation({
    mutationFn: (data: { offerId: number } & PayerChangeOverride) =>
      callJson('offer/detach-payer', data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.detail(vars.offerId),
      });
      queryClient.invalidateQueries({
        queryKey: offerKeys.activity(vars.offerId),
      });
    },
  });
}

export type TPayerKycResult =
  | {
      found: true;
      source: 'local';
      payer: {
        id: number;
        displayName: string;
        clientType: string;
        countryCode: string;
        kycExpiresAt: string;
      };
    }
  | {
      found: true;
      source: 'monday';
      mondayKycItemId: number;
      displayName: string;
      clientType: string;
      countryCode: string;
      kycApprovedAt: string;
      kycExpiresAt: string;
    }
  | {
      found: true;
      source: 'contact_kyc';
      contactKycPulseId: number;
      displayName: string;
      clientType: string;
      countryCode: string;
      kycApprovedAt: string;
      kycExpiresAt: string;
    }
  | { found: false };

export function useCheckPayerKyc() {
  return useMutation({
    mutationFn: (data: { contactId: number }) =>
      callJson<TPayerKycResult>('offer/check-payer-kyc', data),
  });
}

export type TSimulationInput = {
  offerId: number;
  paymentMethod: 'Bank Transfer' | 'Cryptocurrency';
  clientType: 'Individual' | 'Legal Entity';
  country: string;
  vatNumber?: string;
  currency?: 'EUR' | 'USD';
  /** The customer's choices. Omitted simulates every service in the offer. */
  selection?: TSimulationSelection;
};

/** Shaped exactly as a customer chooses: one tier per package block,
 *  individual services per service block and per standalone item. */
export type TSimulationSelection = {
  proposals: {
    cpId: number;
    blocks: {
      uuid: string;
      selectedTier: number | null;
      selectedServiceIds: number[];
    }[];
  }[];
  standaloneServiceIds: number[];
};

export type TExclusionDetail = {
  companyId: number;
  companyName: string;
  ruleType: string;
  ruleKey: string;
};

export type TSimulationReport = {
  report: {
    selectedCompany: TInvoicingCompany;
    exclusions: TExclusionDetail[];
    eligibleCompanies: TInvoicingCompany[];
    allCompanies: TInvoicingCompany[];
    context: {
      serviceJurisdiction: string;
      serviceJurisdictions: string[];
      registrationJurisdictions: string[];
      industries: string[];
      payingPartyCountry: string;
      paymentMethod: string;
      serviceCategories: string[];
      currency: string;
    };
    vatCalculation: {
      appliedRate: number;
      isDomestic: boolean;
      buyerCountry: string;
      sellerCountry: string;
    };
    usedFallback: boolean;
  };
  simulatedServiceIds: number[];
  /** Jurisdictions where a company is actually being registered - the only
   *  ones that bar an invoicing entity. */
  registrationJurisdictions: string[];
  proposalContext: {
    jurisdictions: string[];
    serviceCategories: string[];
    industries: string[];
  };
  agents: Array<{
    id: number;
    name: string;
    acceptsFiat: boolean;
    acceptsCrypto: boolean;
    services: string[];
  }>;
};

export type LegalRequestType =
  | 'legal_advice'
  | 'nda'
  | 'evaluation_lo'
  | 'contract_review';

export type TLegalRequest = {
  id: number;
  offerId: number;
  mondayItemId: number;
  subject: string;
  requestType: LegalRequestType;
  mondayStatus: string;
  hasUnread: boolean;
  createdBy: number;
  createdByEmail: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
};

export type TLegalRequestAsset = {
  id: string;
  name: string;
  url: string;
  publicUrl: string;
  fileExtension: string;
  fileSize: number;
};

export type TLegalRequestUpdate = {
  id: string;
  body: string;
  textBody: string;
  createdAt: string;
  creator: { id: string; name: string; photo_thumb: string };
  assets: TLegalRequestAsset[];
  replies: Omit<TLegalRequestUpdate, 'replies'>[];
};

export function useLegalRequests(offerId: number | undefined) {
  return useQuery({
    queryKey: offerKeys.legalRequests(offerId!),
    queryFn: () =>
      callGet<TLegalRequest[]>('offer/legal-requests', { offerId }),
    enabled: !!offerId,
    refetchInterval: 30_000,
  });
}

export function useCreateLegalRequest() {
  return useMutation({
    mutationFn: (data: {
      offerId: number;
      requestType: LegalRequestType;
      inquiry: string;
      jurisdictions: string[];
      details: Record<string, unknown>;
      files?: File[];
    }) => {
      const formData = new FormData();
      const { files, ...payload } = data;
      formData.append('payload', JSON.stringify(payload));
      for (const file of files ?? []) formData.append('files', file);
      return call<TLegalRequest>('offer/legal-request-create', {
        body: formData,
      }).then((r) => r.json());
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.legalRequests(vars.offerId),
      });
    },
  });
}

export function useLegalRequestUpdates(legalRequestId: number | undefined) {
  return useQuery({
    queryKey: [
      ...offerKeys.all,
      'legal-request-updates',
      legalRequestId,
    ] as const,
    queryFn: () =>
      callGet<TLegalRequestUpdate[]>('offer/legal-request-updates', {
        legalRequestId,
      }),
    enabled: !!legalRequestId,
    refetchInterval: 30_000,
  });
}

export function useCreateLegalRequestReply() {
  return useMutation({
    mutationFn: (data: {
      legalRequestId: number;
      body: string;
      parentUpdateId?: string;
      files?: File[];
    }) => {
      const formData = new FormData();
      formData.append('legalRequestId', String(data.legalRequestId));
      formData.append('body', data.body);
      if (data.parentUpdateId)
        formData.append('parentUpdateId', data.parentUpdateId);
      for (const file of data.files ?? []) formData.append('files', file);
      return call<{ id: string }>('offer/legal-request-update', {
        body: formData,
      }).then((r) => r.json());
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...offerKeys.all,
          'legal-request-updates',
          vars.legalRequestId,
        ],
      });
    },
  });
}

export function useMarkLegalRequestRead() {
  return useMutation({
    mutationFn: (data: { legalRequestId: number; offerId: number }) =>
      callJson<{ ok: true }>('offer/legal-request-mark-read', {
        legalRequestId: data.legalRequestId,
      }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: offerKeys.legalRequests(vars.offerId),
      });
    },
  });
}

export type TOfferSelectionStructure = {
  structure: TCustomerSelections;
  /** Whether the customer has engaged at all. Declining every package is a
   *  real choice, so an empty selection is not evidence they chose nothing. */
  hasCustomerChoices: boolean;
};

export function useOfferSelectionStructure(offerId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...offerKeys.all, 'selection-structure', offerId],
    queryFn: () =>
      callJson<TOfferSelectionStructure>(
        'invoicing/offer-selection-structure',
        { offerId },
      ),
    enabled,
  });
}

export const useSimulateCompanySelection = () => {
  return useMutation(
    {
      mutationFn: (req: TSimulationInput) =>
        call<TSimulationReport>('invoicing/simulate-company-selection', {
          json: req,
        }).then((res) => res.json()),
    },
    queryClient,
  );
};
